import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, XCircle, FileSpreadsheet } from "lucide-react";

export function InvoiceImportModal({ open, onOpenChange, clientId, onImported }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const reset = () => { setPreview(null); setResult(null); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/invoice-import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `template_import_invoice_sales.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh template"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoice-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    finally { setLoading(false); e.target.value = ""; }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/invoice-import/commit", { client_id: clientId, rows: preview.rows });
      const failed = (data.errors || []).length;
      const msg = `${data.inserted} invoice dibuat, ${data.skipped} dilewati (nomor sudah ada)`;
      if (failed) toast.warning(`${msg}, ${failed} gagal — lihat rincian`, { id: "inv-import" });
      else toast.success(msg, { id: "inv-import" });
      if (onImported) onImported();
      if ((data.errors || []).length) setResult(data);
      else { onOpenChange(false); reset(); }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const cols = preview?.rows?.[0] ? Object.keys(preview.rows[0].data) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-5xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" /> Import Invoice Sales via Excel</DialogTitle>
        </DialogHeader>
        {!preview && (
          <div className="py-6 space-y-4">
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200">
              Satu invoice bisa punya banyak barang / jasa: baris item berikutnya <b>cukup isi invoice_no yang sama</b> (atau AUTO-1, AUTO-2) dan kolom item
              (item_description, item_type, qty, unit_price). Tanggal, pelanggan, jenis tunai/dp/pelunasan, nominal DP, diskon, PPN, PPh &amp; rekening bank otomatis mengikuti baris pertama invoice tersebut.
              Termin & pelunasan cukup mengisi <b>ref_invoice_no</b> invoice DP pertama. Isi <b>payment_date</b> untuk mencatat pembayaran.
              Bank default, catatan kaki & tanda tangan mengikuti Pengaturan Invoice. Nomor invoice yang sudah ada dilewati (tidak ditimpa).
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-4">
              <div><p className="text-sm font-medium">1. Unduh template</p><p className="text-xs text-muted-foreground">Isi sesuai contoh, lalu unggah kembali.</p></div>
              <Button variant="outline" onClick={downloadTemplate} data-testid="invoice-import-template"><Download className="h-4 w-4 mr-2" />Template</Button>
            </div>
            <label className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border p-10 cursor-pointer hover:bg-secondary/50 transition-colors" data-testid="invoice-import-dropzone">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">{loading ? "Memproses..." : "2. Pilih file Excel / CSV"}</span>
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={onFile} data-testid="invoice-import-file" />
            </label>
          </div>
        )}
        {preview && !result && (
          <div className="space-y-3 min-w-0">
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="flex items-center gap-1.5"><FileSpreadsheet className="h-4 w-4 text-primary" /> Invoice: <b className="num">{preview.invoices}</b></span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Baris valid: <b className="num">{preview.valid}</b></span>
              <span className="flex items-center gap-1.5"><XCircle className="h-4 w-4 text-rose-600" /> Error: <b className="num">{preview.invalid}</b></span>
            </div>
            <div className="max-h-[50vh] max-w-full overflow-auto rounded-md border border-border">
              <Table>
                <TableHeader className="sticky top-0 bg-card"><TableRow>
                  <TableHead className="w-12">Baris</TableHead>
                  {cols.map((c) => <TableHead key={c} className="text-xs whitespace-nowrap">{c}</TableHead>)}
                  <TableHead>Validasi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.row} className={r.valid ? "" : "bg-rose-50 dark:bg-rose-950/30"} data-testid={`invoice-import-row-${r.row}`}>
                      <TableCell className="num">{r.row}</TableCell>
                      {cols.map((c) => <TableCell key={c} className={`text-xs whitespace-nowrap ${r.inherited?.includes(c) ? "italic text-muted-foreground" : ""}`} title={r.inherited?.includes(c) ? "Mengikuti baris pertama invoice" : undefined}>{String(r.data[c])}</TableCell>)}
                      <TableCell className="text-xs">
                        {r.errors.map((e, i) => <div key={i} className="flex items-center gap-1 text-rose-600"><XCircle className="h-3 w-3" />{e}</div>)}
                        {r.valid && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />OK</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        {result && (
          <div className="space-y-3" data-testid="invoice-import-result">
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Dibuat: <b className="num" data-testid="invoice-import-inserted">{result.inserted}</b></span>
              <span>Dilewati (nomor sudah ada): <b className="num">{result.skipped}</b></span>
              <span className="flex items-center gap-1.5"><XCircle className="h-4 w-4 text-rose-600" /> Gagal: <b className="num">{result.errors.length}</b></span>
            </div>
            <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
              <Table>
                <TableHeader className="sticky top-0 bg-card"><TableRow><TableHead>Invoice</TableHead><TableHead>Penyebab</TableHead></TableRow></TableHeader>
                <TableBody>
                  {result.errors.map((er, i) => (
                    <TableRow key={`${er.invoice_no}-${i}`} data-testid={`invoice-import-error-${i}`}>
                      <TableCell className="text-xs num whitespace-nowrap">{er.invoice_no}</TableCell>
                      <TableCell className="text-xs text-rose-600">{er.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <DialogFooter>
          {preview && !result && <Button variant="outline" onClick={reset}>Pilih file lain</Button>}
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} data-testid="invoice-import-close">Tutup</Button>
          {preview && !result && <Button onClick={commit} disabled={committing || preview.valid === 0} data-testid="invoice-import-commit-btn">{committing ? "Mengimport..." : `Import ${preview.invoices} invoice`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
